import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { Block as BlockIcon } from '@mui/icons-material';

/**
 * Componente que bloqueia toda a interface quando há rate limit ativo
 * Persiste o estado mesmo após refresh da página
 */
export const RateLimitBlock = ({ isBlocked, waitTime, onUnblock }) => {
  const [remainingTime, setRemainingTime] = useState(waitTime || 0);
  const [isActive, setIsActive] = useState(isBlocked);

  useEffect(() => {
    // Verificar se há bloqueio persistido no localStorage
    const checkPersistedBlock = () => {
      const persisted = localStorage.getItem('rateLimitBlock');
      if (persisted) {
        try {
          const { blockedUntil } = JSON.parse(persisted);
          const now = Date.now();
          const timeRemaining = Math.ceil((blockedUntil - now) / 1000);
          
          if (timeRemaining > 0) {
            setIsActive(true);
            setRemainingTime(timeRemaining);
            return true;
          } else {
            // Bloqueio expirado, limpar
            localStorage.removeItem('rateLimitBlock');
            setIsActive(false);
            if (onUnblock) {
              onUnblock();
            }
            return false;
          }
        } catch (error) {
          console.error('Erro ao verificar bloqueio persistido:', error);
          localStorage.removeItem('rateLimitBlock');
          setIsActive(false);
          return false;
        }
      }
      // Se não há bloqueio persistido, garantir que está desbloqueado
      setIsActive(false);
      return false;
    };

    // Verificar bloqueio persistido ao montar
    const hasPersisted = checkPersistedBlock();
    
    // Se não há bloqueio persistido mas a prop diz que está bloqueado, usar a prop
    if (!hasPersisted && isBlocked) {
      setIsActive(true);
      if (waitTime) {
        setRemainingTime(waitTime);
      }
    } else if (!hasPersisted && !isBlocked) {
      // Garantir que está desbloqueado se não há bloqueio e prop diz que não está bloqueado
      setIsActive(false);
    }
  }, [isBlocked, waitTime, onUnblock]);

  useEffect(() => {
    // Atualizar estado quando prop mudar
    if (isBlocked && waitTime && waitTime > 0) {
      setIsActive(true);
      setRemainingTime(waitTime);
      
      // Salvar no localStorage para persistir após refresh
      const blockedUntil = Date.now() + (waitTime * 1000);
      localStorage.setItem('rateLimitBlock', JSON.stringify({
        blockedUntil,
        waitTime
      }));
    } else if (!isBlocked) {
      // Verificar se ainda há bloqueio persistido antes de desativar
      const persisted = localStorage.getItem('rateLimitBlock');
      if (!persisted) {
        setIsActive(false);
        localStorage.removeItem('rateLimitBlock');
      } else {
        // Se há bloqueio persistido mas prop diz que não está bloqueado, verificar se expirou
        try {
          const { blockedUntil } = JSON.parse(persisted);
          const now = Date.now();
          const timeRemaining = Math.ceil((blockedUntil - now) / 1000);
          if (timeRemaining <= 0) {
            localStorage.removeItem('rateLimitBlock');
            setIsActive(false);
          }
        } catch (error) {
          localStorage.removeItem('rateLimitBlock');
          setIsActive(false);
        }
      }
    }
  }, [isBlocked, waitTime]);

  useEffect(() => {
    if (!isActive || remainingTime <= 0) {
      if (remainingTime <= 0 && isActive) {
        // Tempo expirado - limpar bloqueio
        localStorage.removeItem('rateLimitBlock');
        setIsActive(false);
        if (onUnblock) {
          onUnblock();
        }
      } else {
        setIsActive(false);
        if (onUnblock) {
          onUnblock();
        }
      }
      return;
    }

    // Atualizar contador a cada segundo
    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        const newTime = prev - 1;
        
        // Verificar também o localStorage para sincronizar entre abas
        const persisted = localStorage.getItem('rateLimitBlock');
        if (persisted) {
          try {
            const { blockedUntil } = JSON.parse(persisted);
            const now = Date.now();
            const timeRemaining = Math.ceil((blockedUntil - now) / 1000);
            
            if (timeRemaining <= 0) {
              // Tempo expirado - limpar bloqueio
              localStorage.removeItem('rateLimitBlock');
              setIsActive(false);
              if (onUnblock) {
                onUnblock();
              }
              return 0;
            }
            
            return Math.max(timeRemaining, 0);
          } catch (error) {
            return newTime;
          }
        }
        
        if (newTime <= 0) {
          // Tempo expirado - limpar bloqueio
          localStorage.removeItem('rateLimitBlock');
          setIsActive(false);
          if (onUnblock) {
            onUnblock();
          }
          return 0;
        }
        
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, remainingTime, onUnblock]);

  // Não renderizar se não estiver bloqueado
  // IMPORTANTE: Retornar null imediatamente para não interferir com React Router
  if (!isActive) {
    return null;
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const minutes = Math.floor(remainingTime / 60);
  const seconds = remainingTime % 60;
  const timeMessage = minutes > 0 
    ? `${minutes} minuto${minutes > 1 ? 's' : ''} e ${seconds} segundo${seconds !== 1 ? 's' : ''}`
    : `${remainingTime} segundo${remainingTime !== 1 ? 's' : ''}`;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
        pointerEvents: 'auto', // Garantir que eventos funcionem quando bloqueado
      }}
      onClick={(e) => {
        // Prevenir que cliques passem através do overlay quando bloqueado
        e.stopPropagation();
      }}
    >
      <Paper
        elevation={24}
        sx={{
          padding: 4,
          maxWidth: 500,
          width: '90%',
          textAlign: 'center',
          backgroundColor: '#1a1a1a',
          color: '#fff',
        }}
      >
        <BlockIcon sx={{ fontSize: 80, color: '#f44336', mb: 2 }} />
        
        <Typography variant="h4" gutterBottom sx={{ color: '#f44336', fontWeight: 'bold' }}>
          API Bloqueada
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, color: '#ccc' }}>
          O limite de requisições foi atingido. A API Sponte está temporariamente bloqueada.
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{ color: '#f44336', mb: 2 }}
          />
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>
            {formatTime(remainingTime)}
          </Typography>
          <Typography variant="body2" sx={{ color: '#999' }}>
            Aguarde {timeMessage} antes de tentar novamente
          </Typography>
        </Box>
        
        <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic', mt: 2 }}>
          Esta tela permanecerá bloqueada até que o tempo expire.
          <br />
          Atualizar a página não removerá o bloqueio.
        </Typography>
      </Paper>
    </Box>
  );
};

